import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { MapReuseStrategy } from './map-reuse.strategy';
import { AdminRouteReuseStrategy } from './admin-route-reuse.strategy';

/**
 * Composes the two route-reuse strategies the app needs:
 *   - MapReuseStrategy   : keeps `/map` alive (Mapbox + Three.js cold load)
 *   - AdminRouteReuseStrategy : multi-key cache for admin tabs
 *
 * Routes that aren't claimed by either strategy mount/unmount normally.
 */
@Injectable({ providedIn: 'root' })
export class CompositeRouteReuseStrategy implements RouteReuseStrategy {
  private readonly mapStrategy = new MapReuseStrategy();
  private readonly adminStrategy = inject(AdminRouteReuseStrategy);

  private isMapRoute(route: ActivatedRouteSnapshot): boolean {
    // Only the employee /map (flagged keepWarmMap) — NOT the admin estate map
    // (/admin/map), which also has leaf path 'map' and must be handled by the
    // admin tab strategy instead. Keying on path alone cross-wired the two.
    return route.routeConfig?.data?.['keepWarmMap'] === true;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (this.isMapRoute(route)) return this.mapStrategy.shouldDetach(route);
    if (this.adminStrategy.isAdminRoute(route)) return this.adminStrategy.shouldDetach(route);
    return false;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (this.isMapRoute(route)) return this.mapStrategy.store(route, handle);
    if (this.adminStrategy.isAdminRoute(route)) return this.adminStrategy.store(route, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (this.isMapRoute(route)) return this.mapStrategy.shouldAttach(route);
    if (this.adminStrategy.isAdminRoute(route)) return this.adminStrategy.shouldAttach(route);
    return false;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (this.isMapRoute(route)) return this.mapStrategy.retrieve(route);
    if (this.adminStrategy.isAdminRoute(route)) return this.adminStrategy.retrieve(route);
    return null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (this.isMapRoute(future) && this.isMapRoute(curr)) {
      return this.mapStrategy.shouldReuseRoute(future, curr);
    }
    if (this.adminStrategy.isAdminRoute(future) && this.adminStrategy.isAdminRoute(curr)) {
      return this.adminStrategy.shouldReuseRoute(future, curr);
    }
    return future.routeConfig === curr.routeConfig;
  }
}
